// Explore › Logs' query bar (APS-198): a few words turned into the structured query the API
// takes (POST /explore/logs). The API builds the SQL; this only reads the words.
//
//   severity>=warn            at least this severity (also severity:warn)
//   service.version:"5.2"     a field or attribute equals a value (quotes for spaces)
//   -os.name:Android          ... does not equal it
//   has:cart.id / -has:cart.id   the attribute is there, or is not
//   body:"payment failed"     the body contains this text (also any bare words)
//
// Anything else is an error with its position, and nothing is sent.

export const SEVERITY_NAMES = ['trace', 'debug', 'info', 'warn', 'error', 'fatal'] as const
export type SeverityName = (typeof SEVERITY_NAMES)[number]

export interface LogFilter {
  field: string
  op: 'eq' | 'neq' | 'exists' | 'not_exists'
  value?: string
}

export interface LogQuery {
  severity?: SeverityName
  body?: string
  filters: LogFilter[]
}

export type LogQueryError =
  | { code: 'quote'; position: number }
  | { code: 'severity'; position: number; value: string }
  | { code: 'field'; position: number; value: string }
  | { code: 'empty'; position: number; value: string }
  | { code: 'tooMany'; position: number }
  | { code: 'tooLong'; position: number }

/** The API's limits (apps/api read/explore.ts). */
export const FIELD = /^[A-Za-z0-9_.:/-]{1,128}$/
export const MAX_FILTERS = 20
export const MAX_VALUE = 256
export const MAX_BODY = 200

export interface Token {
  /** The token's text, quotes taken away. */
  text: string
  /** Where the token starts, 1-based, for the error. */
  position: number
  /** The whole token was quoted: a phrase, never a field. */
  phrase: boolean
  /** Where `key:` ends in `text`, when a colon came before any quote. */
  colon: number
}

export function tokenize(input: string): Token[] | LogQueryError {
  const tokens: Token[] = []
  let i = 0
  while (i < input.length) {
    if (/\s/.test(input[i] as string)) {
      i++
      continue
    }
    const start = i
    let text = ''
    let colon = -1
    let quotedYet = false
    const phrase = input[i] === '"'
    while (i < input.length && !/\s/.test(input[i] as string)) {
      const ch = input[i] as string
      if (ch === '"') {
        const end = input.indexOf('"', i + 1)
        if (end < 0) return { code: 'quote', position: i + 1 }
        text += input.slice(i + 1, end)
        quotedYet = true
        i = end + 1
        continue
      }
      if (ch === ':' && colon < 0 && !quotedYet) colon = text.length
      text += ch
      i++
    }
    tokens.push({ text, position: start + 1, phrase, colon })
  }
  return tokens
}

/** Reads the query bar's words; an error says what and where. */
export function parseLogQuery(input: string): { query: LogQuery } | { error: LogQueryError } {
  const tokens = tokenize(input)
  if (!Array.isArray(tokens)) return { error: tokens }
  const query: LogQuery = { filters: [] }
  const words: string[] = []
  for (const token of tokens) {
    if (token.phrase || token.colon < 0) {
      const severity = token.phrase ? null : /^(?:severity|level)>=(.*)$/i.exec(token.text)
      if (!severity) {
        words.push(token.text)
        continue
      }
      const name = (severity[1] ?? '').toLowerCase()
      if (!(SEVERITY_NAMES as readonly string[]).includes(name))
        return { error: { code: 'severity', position: token.position, value: severity[1] ?? '' } }
      query.severity = name as SeverityName
      continue
    }
    const negated = token.text.startsWith('-')
    const key = token.text.slice(negated ? 1 : 0, token.colon)
    const value = token.text.slice(token.colon + 1)
    if (/^(severity|level)$/i.test(key) && !negated) {
      const name = value.toLowerCase()
      if (!(SEVERITY_NAMES as readonly string[]).includes(name))
        return { error: { code: 'severity', position: token.position, value } }
      query.severity = name as SeverityName
      continue
    }
    if (key === 'has') {
      if (!FIELD.test(value)) return { error: { code: 'field', position: token.position, value } }
      query.filters.push({ field: value, op: negated ? 'not_exists' : 'exists' })
      continue
    }
    if (!FIELD.test(key)) return { error: { code: 'field', position: token.position, value: key } }
    if (!value) return { error: { code: 'empty', position: token.position, value: key } }
    if (value.length > MAX_VALUE) return { error: { code: 'tooLong', position: token.position } }
    if (key === 'body' && !negated) {
      words.push(value)
      continue
    }
    query.filters.push({ field: key, op: negated ? 'neq' : 'eq', value })
  }
  if (query.filters.length > MAX_FILTERS)
    return { error: { code: 'tooMany', position: tokens.at(-1)?.position ?? 1 } }
  const body = words.join(' ').trim()
  if (body.length > MAX_BODY) return { error: { code: 'tooLong', position: 1 } }
  if (body) query.body = body
  return { query }
}

const quote = (v: string) => (/[\s"]/.test(v) || v === '' ? `"${v.replace(/"/g, '')}"` : v)

/** The query bar's words for a query: what a chip's removal or a click on a value writes back. */
export function formatLogQuery(query: LogQuery): string {
  const parts: string[] = []
  if (query.severity) parts.push(`severity>=${query.severity}`)
  for (const f of query.filters) {
    if (f.op === 'exists') parts.push(`has:${f.field}`)
    else if (f.op === 'not_exists') parts.push(`-has:${f.field}`)
    else parts.push(`${f.op === 'neq' ? '-' : ''}${f.field}:${quote(f.value ?? '')}`)
  }
  if (query.body) parts.push(`body:${quote(query.body)}`)
  return parts.join(' ')
}
